import {
  Search,
  ShieldCheck,
  UserCog,
} from "lucide-react";
import { FormEvent, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, json, normalizePage, queryString } from "../../lib/api";
import type { AuditLog, Page, User } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { Badge, formatDate } from "../../components/Badge";
import { SearchInput, Select } from "../../components/Form";
import {
  Empty,
  ErrorState,
  Failure,
  Loading,
  Pagination,
  Success,
} from "../../components/State";

interface AdminUser extends User {
  created_at?: string;
  last_login_at?: string;
}
interface AccessRole {
  id: string;
  code: string;
  name: string;
  description: string;
  scope: string;
  is_system: boolean;
  status: string;
  permissions: Array<{ code: string; name: string; description: string; sensitive: boolean }>;
}
export function AdminUsersPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const keyword = params.get("keyword") || "";
  const status = params.get("status") || "";
  const [draft, setDraft] = useState(keyword);
  const result = useAsync(
    async () =>
      normalizePage(
        await api<Page<AdminUser>>(
          `/admin/accounts${queryString({ page, pageSize: 20, q: keyword, status })}`,
        ),
      ),
    [page, keyword, status],
  );
  const accessModel = useAsync(() => api<{ items: AccessRole[] }>("/admin/access-model"), []);
  const [editing, setEditing] = useState<AdminUser>();
  const [roles, setRoles] = useState<string[]>([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy(editing.id);
    setError("");
    try {
      await api(`/admin/accounts/${editing.id}`, {
        method: "PATCH",
        body: json({ status: editing.status || "active", roles }),
      });
      await result.retry();
      setEditing(undefined);
      setMessage("账号角色已更新");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "更新失败");
    } finally {
      setBusy("");
    }
  };
  const toggle = async (item: AdminUser) => {
    setBusy(item.id);
    setError("");
    setMessage("");
    try {
      const response = await api<{ completed?: boolean; lifecycle_status?: string }>(`/admin/accounts/${item.id}`, {
        method: "PATCH",
        body: json({
          status: item.status === "disabled" ? "active" : "disabled",
          roles: item.roles,
        }),
      });
      await result.retry();
      setMessage(response.completed === false
        ? "恢复申请已记录，需另一名超级管理员复核"
        : item.status === "disabled" ? "账号已恢复" : "账号已停用");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "更新失败");
    } finally {
      setBusy("");
    }
  };
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>ACCESS CONTROL</span>
          <h1>账号与权限</h1>
          <p>超级管理员可配置后台角色并启停账号。</p>
        </div>
      </header>
      {message && <Success>{message}</Success>}
      {error && <Failure>{error}</Failure>}
      <form
        className="admin-filterbar"
        onSubmit={(e) => {
          e.preventDefault();
          patch("keyword", draft);
        }}
      >
        <label>
          <Search size={16} />
          <SearchInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="搜索昵称"
          />
        </label>
        <Select
          value={status}
          onChange={(e) => patch("status", e.target.value)}
        >
          <option value="">全部状态</option>
          <option value="active">正常</option>
          <option value="disabled">已停用</option>
        </Select>
        <button className="primary-button">查询</button>
      </form>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无账号" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>用户</th>
                <th>角色</th>
                <th>注册时间</th>
                <th>最近登录</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {result.data.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.nickname || "未设置昵称"}</strong>
                    <small>{item.masked_phone || item.phone}</small>
                  </td>
                  <td>
                    <div className="tag-row">
                      {item.roles.map((role) => (
                        <Badge key={role}>{roleName(role)}</Badge>
                      ))}
                    </div>
                  </td>
                  <td>{formatDate(item.created_at)}</td>
                  <td>{formatDate(item.last_login_at)}</td>
                  <td>
                    <Badge
                      tone={item.status === "disabled" ? "danger" : "success"}
                    >
                      {item.status === "disabled" ? "已停用" : "正常"}
                    </Badge>
                  </td>
                  <td>
                    <div className="row-actions">
                      <button
                        onClick={() => {
                          setEditing(item);
                          setRoles(item.roles);
                        }}
                      >
                        <UserCog size={14} />
                        配置角色
                      </button>
                      <button
                        className={
                          item.status === "disabled" ? "" : "danger-text"
                        }
                        disabled={busy === item.id}
                        onClick={() => toggle(item)}
                      >
                        {item.status === "disabled" ? "启用" : "停用"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        pageSize={result.data?.page_size || 20}
        total={result.data?.total || 0}
        onChange={(value) => patch("page", String(value))}
      />
      <section className="admin-panel access-model">
        <header><div><span>ROLE PERMISSIONS</span><h2>角色权限矩阵</h2><p>系统角色由迁移版本管理；此处展示当前数据库实际生效的权限集合。</p></div></header>
        {accessModel.loading ? <Loading /> : accessModel.error ? <ErrorState error={accessModel.error} retry={accessModel.retry} /> : <div className="access-role-list">{accessModel.data?.items.map((role) => <article key={role.id}><div><strong>{role.name}</strong><small>{role.code} · {role.scope === "tenant" ? "租户级" : "园区级"}</small><p>{role.description}</p></div><div>{role.permissions.map((permission) => <span className={permission.sensitive ? "sensitive" : ""} title={permission.description} key={permission.code}>{permission.name}<small>{permission.code}</small></span>)}</div></article>)}</div>}
      </section>
      {editing && (
        <div className="modal-layer">
          <button
            className="modal-backdrop"
            onClick={() => setEditing(undefined)}
            aria-label="关闭"
          />
          <form className="modal-card" onSubmit={save}>
            <header>
              <h2>配置账号角色</h2>
              <button type="button" onClick={() => setEditing(undefined)}>
                ×
              </button>
            </header>
            <p className="muted-text">
              {editing.nickname || editing.masked_phone}
            </p>
            <div className="role-options">
              {[
                "registered_user",
                "park_viewer",
                "park_reviewer",
                "park_admin",
                "policy_editor",
                "policy_approver",
                "community_moderator",
                "super_admin",
              ].map((role) => (
                  <label key={role}>
                    <input
                      type="checkbox"
                      checked={roles.includes(role)}
                      onChange={(e) =>
                        setRoles(
                          e.target.checked
                            ? [...roles, role]
                            : roles.filter((x) => x !== role),
                        )
                      }
                    />
                    <span>
                      <strong>{roleName(role)}</strong>
                      <small>{roleDescription(role)}</small>
                    </span>
                  </label>
              ))}
            </div>
            <button className="primary-button" disabled={busy === editing.id}>
              保存角色
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export function AuditLogsPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") || 1);
  const action = params.get("action") || "";
  const resource = params.get("resource_type") || "";
  const result = useAsync(
    async () =>
      normalizePage(
        await api<Page<AuditLog>>(
          `/admin/audit-logs${queryString({ page, pageSize: 30, action, resourceType: resource })}`,
        ),
      ),
    [page, action, resource],
  );
  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };
  return (
    <div>
      <header className="admin-page-header">
        <div>
          <span>AUDIT TRAIL</span>
          <h1>审计日志</h1>
          <p>追踪关键管理操作与业务状态变化。</p>
        </div>
      </header>
      <div className="admin-filterbar">
        <Select
          value={resource}
          onChange={(e) => patch("resource_type", e.target.value)}
        >
          <option value="">全部资源</option>
          <option value="application">入驻申请</option>
          <option value="admission">入驻资格</option>
          <option value="policy">政策</option>
          <option value="user">账号</option>
          <option value="role">角色</option>
        </Select>
        <Select
          value={action}
          onChange={(e) => patch("action", e.target.value)}
        >
          <option value="">全部操作</option>
          <option value="application_decided">申请审核</option>
          <option value="admission_confirmed">确认入驻</option>
          <option value="admission_status_changed">入驻状态变更</option>
          <option value="account_status_changed">账号状态变更</option>
          <option value="role_granted">授予角色</option>
          <option value="role_revoked">撤销角色</option>
          <option value="policy_published">发布政策</option>
          <option value="policy_offlined">下线政策</option>
        </Select>
      </div>
      {result.loading ? (
        <Loading />
      ) : result.error ? (
        <ErrorState error={result.error} retry={result.retry} />
      ) : !result.data?.items.length ? (
        <Empty title="暂无审计记录" />
      ) : (
        <div className="audit-list">
          {result.data.items.map((item) => (
            <article key={item.id}>
              <span>
                <ShieldCheck size={17} />
              </span>
              <div>
                <strong>
                  {item.actor_name || "系统"} · {item.action}
                </strong>
                <p>
                  {item.resource_type} /{" "}
                  {item.resource_id || item.resource_ref || "-"} ·{" "}
                  {item.detail ||
                    `${String(item.before?.status || "-")} -> ${String(item.after?.status || "-")}`}
                </p>
                <small>
                  {formatDate(item.created_at)} · {item.result || "success"}
                </small>
              </div>
            </article>
          ))}
        </div>
      )}
      <Pagination
        page={page}
        pageSize={result.data?.page_size || 30}
        total={result.data?.total || 0}
        onChange={(value) => patch("page", String(value))}
      />
    </div>
  );
}
function roleName(role: string) {
  return (
    (
      {
        registered_user: "注册用户",
        park_viewer: "园区只读",
        park_reviewer: "入驻审核员",
        park_admin: "园区管理员",
        policy_editor: "政策编辑",
        policy_approver: "政策审批",
        community_moderator: "社区运营",
        super_admin: "超级管理员",
      } as Record<string, string>
    )[role] || role
  );
}
function roleDescription(role: string) {
  return (
    (
      {
        registered_user: "使用用户端服务",
        park_viewer: "只读查看运营数据",
        park_reviewer: "处理入驻审核任务",
        park_admin: "可进入园区管理后台",
        policy_editor: "创建、编辑并送审政策",
        policy_approver: "独立审批政策版本",
        community_moderator: "管理公告与论坛入口",
        super_admin: "可管理账号与权限",
      } as Record<string, string>
    )[role] || ""
  );
}
